//packages
import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import { Menu as MuiMenu, MenuItem, Tooltip } from "@mui/material";
//stores
import { useAuth } from "../context/AuthContext";
//style
import "../style/header.css";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageProvider";
import {
  LEARNER_HOME_PREVIEW_PATH,
  COMPOSER_HOME_PATH,
  isLearnerExperiencePath,
  clearLearnerViewMode,
  isLearnerViewModeActive,
} from "../utils/learnerHomePreview";

const HEADER_MENU_ITEMS = [
  { name: "Content editor", link: COMPOSER_HOME_PATH, contentProvider: true },
  { name: "KPIs", link: "/admin/KPIs", contentProvider: true },
  { name: "Conversation history", link: "/admin/conversation-history", contentProvider: true },
  { name: "Interactive Tools", link: "/admin/interactive-tools", contentProvider: false },
  { name: "Users & organizations", link: "/admin/user-management", contentProvider: false },
  { name: "Avatars & Voices", link: "/admin/avatars-voices", contentProvider: false },
  { name: "Bug reports", link: "/admin/bugs", contentProvider: false },
  { name: "Costs", link: "/admin/costs", contentProvider: false },
];

const isMenuPathActive = (pathname, itemLink) => {
  if (itemLink === COMPOSER_HOME_PATH) {
    return (
      pathname === COMPOSER_HOME_PATH ||
      pathname.startsWith("/composer/") ||
      pathname.startsWith("/admin/courses")
    );
  }
  return pathname === itemLink || pathname.startsWith(`${itemLink}/`);
};

//this component renders the app header
const PageHeader = ({
  centerLogo = false,
  courseControl = null,
  showMenuButton = null,
  onMenuButtonClick = null,
  /**
   * `user-home` — open learner homepage `/` in a new tab (from composer).
   * `composer-home` — open content editor home `/admin` in a new tab (from learner views); only shown for staff who can use the header nav.
   */
  headerPreviewMode = null,
  /** Optional override for the profile chip; when omitted, name and org line come from the signed-in user when available. */
  composerUserProfile = null,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerNavOpen, setHeaderNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuAnchor = useRef(null);
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const { logout, isAdmin, isContentProvider, user } = useAuth();
  const canUseHeaderNav = Boolean(isAdmin || isContentProvider);
  const inLearnerViewMode = isLearnerViewModeActive();
  const useComposerChrome = canUseHeaderNav && !inLearnerViewMode;
  const shouldUseCenteredHeader = true;
  const canShowMenuButton = useComposerChrome && (showMenuButton == null ? true : Boolean(showMenuButton));
  const visibleHeaderMenuItems = useMemo(
    () =>
      HEADER_MENU_ITEMS.filter(
        (item) => (isContentProvider === true && item.contentProvider === true) || isAdmin
      ),
    [isAdmin, isContentProvider]
  );

  const resolvedComposerUserProfile = useMemo(() => {
    if (composerUserProfile != null) return composerUserProfile;
    if (!user) return null;
    const name =
      user?.name ||
      (user?.first_name
        ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ""}`
        : "");
    const names = user?.organization_names;
    const orgLine =
      Array.isArray(names) && names.length > 0 ? names.join(", ") : undefined;
    return {
      name: name || "User",
      ...(orgLine ? { role: orgLine } : {}),
    };
  }, [composerUserProfile, user]);

  const composerProfileInitials = useMemo(() => {
    const raw = resolvedComposerUserProfile?.name?.trim();
    if (!raw) return "?";
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    return (parts[0]?.[0] || "?").toUpperCase();
  }, [resolvedComposerUserProfile?.name]);

  const menuOptions = [
    {
      label: t("menu.settings"),
      icon: "/images/settings.png",
      action: () => navigate("/settings"),
    },
    {
      label: t("menu.help"),
      icon: "/images/help.svg",
      action: () => navigate("/help"),
    },
    {
      label: t("menu.privacyPolicy"),
      icon: "/images/privacy-policy.svg",
      action: () => navigate("/privacy-policy"),
    },
    {
      label: t("menu.logout"),
      icon: "/images/logout.png",
      action: logout,
    },
  ];


  const closeMenu = () => {
    setMenuOpen(false);
  };

  const closeHeaderNav = () => {
    setHeaderNavOpen(false);
  };

  useEffect(() => {
    setHeaderNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname === COMPOSER_HOME_PATH ||
      location.pathname.startsWith(`${COMPOSER_HOME_PATH}/`) ||
      location.pathname === "/admin" ||
      location.pathname.startsWith("/admin/courses")
    ) {
      clearLearnerViewMode();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!headerNavOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeHeaderNav();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [headerNavOpen]);

  const handleMenuButtonClick = () => {
    if (typeof onMenuButtonClick === "function") {
      onMenuButtonClick();
      return;
    }
    setHeaderNavOpen((prev) => !prev);
  };

  const showComposerInLearner =
    canUseHeaderNav &&
    isLearnerExperiencePath(location.pathname) &&
    location.pathname !== COMPOSER_HOME_PATH &&
    !location.pathname.startsWith(`${COMPOSER_HOME_PATH}/`);

  const headerPreview =
    headerPreviewMode === "user-home"
      ? {
          path: LEARNER_HOME_PREVIEW_PATH,
          title: "Open learner home in a new tab",
          Icon: HomeOutlinedIcon,
        }
      : headerPreviewMode === "composer-home" && canUseHeaderNav
        ? {
            path: COMPOSER_HOME_PATH,
            title: "Open content editor in a new tab",
            Icon: BrushOutlinedIcon,
          }
        : showComposerInLearner
          ? {
              path: COMPOSER_HOME_PATH,
              title: "Open content editor in a new tab",
              Icon: BrushOutlinedIcon,
            }
          : null;

  const logoHref =
    isLearnerViewModeActive() && canUseHeaderNav
      ? "/"
      : canUseHeaderNav
        ? COMPOSER_HOME_PATH
        : "/";

  const useComposerLogo = useComposerChrome;

  return (
    <>
      <div className={`page-header${shouldUseCenteredHeader ? " page-header-centered-logo" : ""}`}>
        {shouldUseCenteredHeader && (
          <div className="header-left-controls">
            {canShowMenuButton ? (
              <button
                type="button"
                className="header-menu-toggle-btn"
                aria-label="Open menu"
                aria-haspopup="menu"
                aria-expanded={headerNavOpen}
                onClick={handleMenuButtonClick}
              >
                <MenuIcon />
              </button>
            ) : (
              <span className="header-menu-toggle-spacer" aria-hidden="true" />
            )}
          </div>
        )}
        <div className="header-logo-wrapper">
          <Link to={logoHref}>
            <img
              src={useComposerLogo ? "/images/arrows-composer-logo.png" : "/images/logo.png"}
              alt={useComposerLogo ? "Arrows Composer" : "Arrows Icon"}
              className={`header-logo-icon${useComposerLogo ? " header-logo-icon-composer" : ""}`}
            />
          </Link>
        </div>
        <div className="right-icons">
          {courseControl}
          {headerPreview ? (
            <Tooltip title={headerPreview.title} arrow placement="bottom">
              <button
                type="button"
                className="header-user-preview-btn"
                onClick={() => {
                  const url = `${window.location.origin}${headerPreview.path}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                aria-label={headerPreview.title}
              >
                {createElement(headerPreview.Icon, {
                  className: "header-user-preview-icon",
                })}
              </button>
            </Tooltip>
          ) : null}
          {/* <img
            src="/images/bell.png"
            alt="Alerts"
            className="header-bell-icon"
          /> */}
          {resolvedComposerUserProfile ? (
            <button
              type="button"
              ref={menuAnchor}
              onClick={() => setMenuOpen(true)}
              id="user-settings-button"
              className="header-composer-profile-btn"
              aria-label="Account menu"
              aria-controls={menuOpen ? "user-settings-menu" : undefined}
              aria-haspopup="true"
            >
              <span className="header-composer-profile-avatar" aria-hidden="true">
                {composerProfileInitials}
              </span>
              <span className="header-composer-profile-text">
                <span className="header-composer-profile-name">
                  {resolvedComposerUserProfile.name?.trim() || "User"}
                </span>
                {resolvedComposerUserProfile.role ? (
                  <span className="header-composer-profile-role">
                    {resolvedComposerUserProfile.role}
                  </span>
                ) : null}
              </span>
            </button>
          ) : (
            <img
              ref={menuAnchor}
              onClick={() => setMenuOpen(true)}
              src="/images/user.png"
              alt="User"
              id="user-settings-button"
              className="header-user-icon"
              aria-controls={menuOpen ? "user-settings-menu" : undefined}
            />
          )}
        </div>
      </div>
      <MuiMenu
        open={menuOpen}
        id="user-settings-menu"
        anchorEl={menuAnchor.current}
        onClose={closeMenu}
        MenuListProps={{
          "aria-labelledby": "user-settings-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#262d3b",
            color: "white",
            fontFamily: "Assistant",
            direction: dir
          }
        }}
      >
        {menuOptions.map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              option.action();
              closeMenu();
            }}
            sx={{
              borderTop: !index ? "none" : "1px solid white",
              padding: "1rem 1.5rem",
            }}
          >
            <img
              src={option.icon}
              alt={option.label}
              className="header-menu-icon"
              style={{
                height: "1rem",
                marginInlineEnd: "10px",
              }} 
            />
            {option.label}
          </MenuItem>
        ))}
      </MuiMenu>
      {canShowMenuButton && (
        <>
          <button
            type="button"
            className={`header-nav-overlay${headerNavOpen ? " open" : ""}`}
            onClick={closeHeaderNav}
            aria-label="Close menu"
            tabIndex={headerNavOpen ? 0 : -1}
          />
          <aside
            className={`header-nav-sidebar${headerNavOpen ? " open" : ""}`}
            aria-hidden={!headerNavOpen}
            role="navigation"
            aria-label="Admin navigation"
          >
            <nav className="header-nav-links">
              {visibleHeaderMenuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.link}
                  className={`header-nav-link${
                    isMenuPathActive(location.pathname, item.link) ? " active" : ""
                  }`}
                  onClick={closeHeaderNav}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </aside>
        </>
      )}
    </>
  );
};

export default PageHeader;
